import { useState, useContext, useEffect, useCallback } from "react";
import '../../styles/page-titles.css'
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import { SidebarContext } from "../../AppRoutes";
import { getRelatorioClientesAnalise } from "../../services/api";
import { nowTz } from '../../utils/timezone';
import Loading from "react-loading";
import { FaUsers, FaUserPlus, FaUserCheck, FaStar, FaCrown, FaChartLine, FaRedo } from 'react-icons/fa';
import { MdPersonAdd, MdRepeat, MdTrendingUp } from 'react-icons/md';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, LineChart, Line, AreaChart, Area
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatPriceWithSymbol } from '../../utils/currency';
import { formatPhoneDisplay } from '../../utils/phone';
import regionConfig from '../../config/regionConfig';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    .filters {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    input[type="date"] {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
    }

    button {
        padding: 8px 16px;
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.9;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    @media (max-width: 600px) {
        h1 {
            font-size: 18px;
        }

        .filters {
            width: 100%;
        }

        input[type="date"], button {
            flex: 1;
        }
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
`;

const MetricCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;

    .icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        margin-bottom: 8px;
    }

    .icon.blue { background: rgba(49, 140, 213, 0.1); color: rgb(49, 140, 213); }
    .icon.green { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .icon.orange { background: rgba(255, 152, 0, 0.1); color: #ff9800; }
    .icon.purple { background: rgba(102, 126, 234, 0.1); color: #667eea; }
    .icon.gold { background: rgba(255, 193, 7, 0.1); color: #ffc107; }

    .label {
        font-size: 14px;
        color: #666;
    }

    .value {
        font-size: 28px;
        font-weight: 700;
        color: #333;
    }

    .sub {
        font-size: 12px;
        color: #999;
    }

    @media (max-width: 500px) {
        padding: 14px;
        .value { font-size: 22px; }
    }
`;

const ChartsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
`;

const ChartCard = styled.div`
    background: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .chart-container {
        height: 300px;
        width: 100%;
    }

    @media (max-width: 600px) {
        padding: 16px;

        .chart-container {
            height: 250px;
        }
    }
`;

const FullWidthChart = styled(ChartCard)`
    grid-column: 1 / -1;

    .chart-container {
        height: 350px;
    }
`;

const TopClientsTable = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    margin-bottom: 20px;

    .table-header {
        padding: 20px 24px;
        border-bottom: 1px solid #e2e8f0;

        h3 {
            font-size: 18px;
            font-weight: 600;
            color: #2d3748;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }

    .table-wrapper {
        overflow-x: auto;
    }

    table {
        width: 100%;
        border-collapse: collapse;

        thead {
            background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));

            th {
                padding: 16px;
                text-align: left;
                font-weight: 600;
                font-size: 13px;
                color: white;
                white-space: nowrap;

                &:first-child {
                    padding-left: 24px;
                }
            }
        }

        tbody {
            tr {
                border-bottom: 1px solid #f1f5f9;
                transition: background-color 0.2s ease;

                &:hover {
                    background: #f8fafc;
                }

                td {
                    padding: 14px 16px;
                    color: #2d3748;
                    font-size: 14px;

                    &:first-child {
                        padding-left: 24px;
                    }

                    .rank {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        font-weight: 600;
                    }

                    .rank-1, .rank-2, .rank-3 {
                        color: #ffc107;
                    }

                    .cliente-info {
                        display: flex;
                        flex-direction: column;
                        gap: 2px;

                        .nome {
                            font-weight: 600;
                            color: #2d3748;
                        }

                        .telefone {
                            font-size: 12px;
                            color: #718096;
                        }
                    }

                    .valor {
                        font-weight: 600;
                        color: rgb(49, 140, 213);
                    }

                    .frequencia-badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 4px;
                        padding: 4px 10px;
                        border-radius: 20px;
                        font-size: 12px;
                        font-weight: 600;
                        background: rgba(40, 167, 69, 0.1);
                        color: #28a745;
                    }
                }
            }
        }
    }

    @media (max-width: 768px) {
        table {
            min-width: 600px;
        }
    }
`;

const ClientTypeComparison = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
`;

const ClientTypeCard = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    .icon-container {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 36px;
        margin-bottom: 16px;

        &.novo {
            background: linear-gradient(135deg, rgba(40, 167, 69, 0.1), rgba(76, 217, 100, 0.2));
            color: #28a745;
        }

        &.recorrente {
            background: linear-gradient(135deg, rgba(49, 140, 213, 0.1), rgba(79, 172, 254, 0.2));
            color: rgb(49, 140, 213);
        }
    }

    .title {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin-bottom: 8px;
    }

    .description {
        font-size: 13px;
        color: #666;
        margin-bottom: 16px;
    }

    .stats {
        display: flex;
        gap: 24px;
        margin-top: 8px;

        .stat {
            .value {
                font-size: 24px;
                font-weight: 700;
                color: #333;
            }

            .label {
                font-size: 12px;
                color: #666;
            }
        }
    }

    .percentage-bar {
        width: 100%;
        height: 8px;
        background: #e9ecef;
        border-radius: 4px;
        margin-top: 16px;
        overflow: hidden;

        .fill {
            height: 100%;
            border-radius: 4px;
            transition: width 0.5s ease;

            &.novo {
                background: linear-gradient(135deg, #28a745, #4cd964);
            }

            &.recorrente {
                background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
            }
        }
    }

    .percentage-text {
        margin-top: 8px;
        font-size: 14px;
        font-weight: 600;
        color: #666;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
`;

const COLORS = ['#28a745', '#318CD5'];

const RelatorioClientes = () => {
    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);

    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('reports');

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState({
        resumo: {
            clientes_ativos: 0,
            clientes_novos: 0,
            clientes_recorrentes: 0,
            taxa_retorno: 0,
            ticket_medio_geral: 0,
            pedidos_por_cliente: 0
        },
        novos_vs_recorrentes: {
            novos: { quantidade: 0, faturamento: 0, percentual: 0 },
            recorrentes: { quantidade: 0, faturamento: 0, percentual: 0 }
        },
        frequencia: {},
        top_clientes: []
    });
    const [startDate, setStartDate] = useState(nowTz().subtract(30, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(nowTz().format('YYYY-MM-DD'));

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getRelatorioClientesAnalise(startDate, endDate);
            if (response?.data?.success) {
                setData(response.data);
            }
        } catch (error) {
            console.error('[RELATORIO-CLIENTES] Erro ao buscar dados:', error);
            toast.error(t('common.fetchError'));
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Preparar dados para gráfico de pizza (novos vs recorrentes)
    const clientesPieData = [
        { name: t('customers.newLabel'), value: data.novos_vs_recorrentes?.novos?.quantidade || 0, color: '#28a745' },
        { name: t('customers.recurringLabel'), value: data.novos_vs_recorrentes?.recorrentes?.quantidade || 0, color: '#318CD5' }
    ];

    // Preparar dados para gráfico de frequência (backend retorna objeto)
    const frequenciaData = Object.entries(data.frequencia || {}).map(([key, value]) => ({
        name: key,
        clientes: value
    }));

    // Evolução de clientes (não disponível no backend atual)
    const evolucaoData = [];

    const formatCurrency = (value) => {
        return formatPriceWithSymbol(value || 0);
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            // Verifica se o campo deve ser formatado como moeda
            const isCurrencyField = (name, dataKey) => {
                const currencyKeywords = ['faturamento', 'valor', 'value', 'total', 'ticket', 'preco', 'receita'];
                const lowerName = (name || '').toLowerCase();
                const lowerKey = (dataKey || '').toLowerCase();
                return currencyKeywords.some(kw => lowerName.includes(kw) || lowerKey.includes(kw));
            };

            return (
                <div style={{
                    background: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #e0e0e0'
                }}>
                    <p style={{ margin: 0, fontWeight: 600, color: '#333' }}>{label || payload[0]?.payload?.name}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ margin: '4px 0 0', color: entry.color || '#318CD5', fontSize: '13px' }}>
                            {entry.name}: {isCurrencyField(entry.name, entry.dataKey)
                                ? formatCurrency(entry.value)
                                : entry.value?.toLocaleString?.(regionConfig.currency.locale) || entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };


    return (
        <>
            <PermissionGate permissions={['default']}>
                <Container sidebar={sidebar}>
                    <Header>
                        <div className="page-title-with-icon">
                            <div className="page-icon"><FaUsers /></div>
                            <h1 className="page-title">{t('customers.title')}</h1>
                        </div>
                        <div className="filters" style={{ marginRight: '50px' }}>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            <button onClick={fetchData} disabled={isLoading}>
                                {isLoading ? t('common.loading') : t('common.update')}
                            </button>
                        </div>
                    </Header>

                    {isLoading ? (
                        <LoadingContainer>
                            <Loading type="spin" color="rgb(49, 140, 213)" height={40} width={40} />
                        </LoadingContainer>
                    ) : (
                        <>
                            <CardsGrid>
                                <MetricCard>
                                    <div className="icon blue">
                                        <FaUsers />
                                    </div>
                                    <span className="label">{t('customers.activeClients')}</span>
                                    <span className="value">{data.resumo?.clientes_ativos || 0}</span>
                                    <span className="sub">{t('common.inPeriod')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon green">
                                        <FaUserPlus />
                                    </div>
                                    <span className="label">{t('customers.newClients')}</span>
                                    <span className="value">{data.resumo?.clientes_novos || 0}</span>
                                    <span className="sub">{t('common.ofTotal', { value: parseFloat(data.novos_vs_recorrentes?.novos?.percentual || 0).toFixed(1) })}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon purple">
                                        <FaUserCheck />
                                    </div>
                                    <span className="label">{t('customers.recurringClients')}</span>
                                    <span className="value">{data.resumo?.clientes_recorrentes || 0}</span>
                                    <span className="sub">{t('common.ofTotal', { value: parseFloat(data.novos_vs_recorrentes?.recorrentes?.percentual || 0).toFixed(1) })}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon gold">
                                        <FaRedo />
                                    </div>
                                    <span className="label">{t('customers.returnRate')}</span>
                                    <span className="value">{parseFloat(data.resumo?.taxa_retorno || 0).toFixed(1)}%</span>
                                    <span className="sub">{t('customers.returnRateSub')}</span>
                                </MetricCard>
                            </CardsGrid>

                            <ClientTypeComparison>
                                <ClientTypeCard>
                                    <div className="icon-container novo">
                                        <MdPersonAdd />
                                    </div>
                                    <span className="title">{t('customers.newClientsCard')}</span>
                                    <span className="description">{t('customers.newClientsDesc')}</span>
                                    <div className="stats">
                                        <div className="stat">
                                            <div className="value">{data.novos_vs_recorrentes?.novos?.quantidade || 0}</div>
                                            <div className="label">{t('common.clients')}</div>
                                        </div>
                                        <div className="stat">
                                            <div className="value">
                                                {formatPriceWithSymbol(data.resumo?.ticket_medio_geral || 0)}
                                            </div>
                                            <div className="label">{t('common.avgTicket')}</div>
                                        </div>
                                    </div>
                                    <div className="percentage-bar">
                                        <div
                                            className="fill novo"
                                            style={{ width: `${parseFloat(data.novos_vs_recorrentes?.novos?.percentual || 0)}%` }}
                                        />
                                    </div>
                                    <span className="percentage-text">
                                        {t('customers.clientsOfTotal', { value: parseFloat(data.novos_vs_recorrentes?.novos?.percentual || 0).toFixed(1) })}
                                    </span>
                                </ClientTypeCard>

                                <ClientTypeCard>
                                    <div className="icon-container recorrente">
                                        <MdRepeat />
                                    </div>
                                    <span className="title">{t('customers.recurringClientsCard')}</span>
                                    <span className="description">{t('customers.recurringClientsDesc')}</span>
                                    <div className="stats">
                                        <div className="stat">
                                            <div className="value">{data.novos_vs_recorrentes?.recorrentes?.quantidade || 0}</div>
                                            <div className="label">{t('common.clients')}</div>
                                        </div>
                                        <div className="stat">
                                            <div className="value">
                                                {formatPriceWithSymbol(data.resumo?.ticket_medio_geral || 0)}
                                            </div>
                                            <div className="label">{t('common.avgTicket')}</div>
                                        </div>
                                    </div>
                                    <div className="percentage-bar">
                                        <div
                                            className="fill recorrente"
                                            style={{ width: `${parseFloat(data.novos_vs_recorrentes?.recorrentes?.percentual || 0)}%` }}
                                        />
                                    </div>
                                    <span className="percentage-text">
                                        {t('customers.clientsOfTotal', { value: parseFloat(data.novos_vs_recorrentes?.recorrentes?.percentual || 0).toFixed(1) })}
                                    </span>
                                </ClientTypeCard>
                            </ClientTypeComparison>

                            <ChartsRow>
                                <ChartCard>
                                    <h3>
                                        <FaChartLine />
                                        {t('customers.newVsRecurring')}
                                    </h3>
                                    <div className="chart-container">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={clientesPieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={70}
                                                    outerRadius={110}
                                                    paddingAngle={3}
                                                    dataKey="value"
                                                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                                    labelLine={false}
                                                >
                                                    {clientesPieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                                    ))}
                                                </Pie>
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                </ChartCard>

                                <ChartCard>
                                    <h3>
                                        <MdRepeat />
                                        {t('customers.frequencyDistribution')}
                                    </h3>
                                    <div className="chart-container">
                                        {frequenciaData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={frequenciaData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="name" />
                                                    <YAxis />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Bar dataKey="clientes" fill="#318CD5" radius={[8, 8, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                                                {t('common.noData')}
                                            </div>
                                        )}
                                    </div>
                                </ChartCard>
                            </ChartsRow>

                            {evolucaoData.length > 0 && (
                                <FullWidthChart>
                                    <h3>
                                        <MdTrendingUp />
                                        {t('customers.clientEvolution')}
                                    </h3>
                                    <div className="chart-container">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={evolucaoData}>
                                                <defs>
                                                    <linearGradient id="colorNovos" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#28a745" stopOpacity={0.8}/>
                                                        <stop offset="95%" stopColor="#28a745" stopOpacity={0.1}/>
                                                    </linearGradient>
                                                    <linearGradient id="colorRecorrentes" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#318CD5" stopOpacity={0.8}/>
                                                        <stop offset="95%" stopColor="#318CD5" stopOpacity={0.1}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="data" />
                                                <YAxis />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend />
                                                <Area
                                                    type="monotone"
                                                    dataKey="novos"
                                                    name={t('customers.newLabel')}
                                                    stroke="#28a745"
                                                    fillOpacity={1}
                                                    fill="url(#colorNovos)"
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="recorrentes"
                                                    name={t('customers.recurringLabel')}
                                                    stroke="#318CD5"
                                                    fillOpacity={1}
                                                    fill="url(#colorRecorrentes)"
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </FullWidthChart>
                            )}

                            <TopClientsTable>
                                <div className="table-header">
                                    <h3>
                                        <FaCrown color="#ffc107" />
                                        {t('customers.top15Title')}
                                    </h3>
                                </div>
                                <div className="table-wrapper">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>{t('customers.tableHeaders.rank')}</th>
                                                <th>{t('customers.tableHeaders.client')}</th>
                                                <th>{t('customers.tableHeaders.orders')}</th>
                                                <th>{t('customers.tableHeaders.totalSpent')}</th>
                                                <th>{t('customers.tableHeaders.avgTicket')}</th>
                                                <th>{t('customers.tableHeaders.frequency')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(data.top_clientes || []).slice(0, 15).map((cliente, index) => (
                                                <tr key={cliente.telefone || index}>
                                                    <td>
                                                        <div className={`rank rank-${index + 1}`}>
                                                            {index < 3 && <FaStar color="#ffc107" />}
                                                            #{index + 1}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="cliente-info">
                                                            <span className="nome">{cliente.nome || t('customers.noClientName')}</span>
                                                            <span className="telefone">{formatPhoneDisplay(cliente.telefone_completo || cliente.telefone)}</span>
                                                        </div>
                                                    </td>
                                                    <td>{cliente.pedidos || 0}</td>
                                                    <td className="valor">
                                                        {formatPriceWithSymbol(cliente.faturamento || 0)}
                                                    </td>
                                                    <td>
                                                        {formatPriceWithSymbol(cliente.ticket_medio || 0)}
                                                    </td>
                                                    <td>
                                                        <span className="frequencia-badge">
                                                            <MdRepeat />
                                                            {cliente.pedidos || 0}x
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </TopClientsTable>
                        </>
                    )}
                </Container>
            </PermissionGate>
        </>
    );
};

export default RelatorioClientes;
